import { useCallback, useMemo, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { matterPaths } from '@/routing/paths'
import { ActionBar, type ActionBarAction } from '@/components/action-bar/ActionBar'
import { MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { AiChat, FileUpload, FolderUpload, MoveToFolder, NewFolderPlus, TrashDelete, Upload } from '@/assets/icons'
import { isDirectoryPickerSupported } from '@/user_files/drive/utils/is-directory-picker-supported'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import {
  selectCurrentFolderId,
  selectCurrentFolderDriveVisibleChildren,
  selectIsCurrentFolderTombstoned,
  selectFolderDialogType,
} from '@/user_files/folders/store/selectors'
import { selectCurrentFolderDriveVisibleFiles, selectFilesById } from '@/user_files/files/store/selectors'
import { selectIsUploadingInActiveMatter } from '@/uppy-file-upload/store/selectors'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { FolderList, type DriveRow } from '@/user_files/folders/components/FolderList'
import { CreateFolderDialog } from '@/user_files/folders/components/CreateFolderDialog'
import { RenameItemDialog } from '@/user_files/drive/components/RenameItemDialog'
import { DriveDropzone } from '@/user_files/drive/components/DriveDropzone'
import { DeleteItemsDialog } from '@/user_files/drive/components/DeleteItemsDialog'
import { MoveItemsDialog } from '@/user_files/move/components/MoveItemsDialog'
import { FileUploadDrawer } from '@/user_files/drive/components/file-upload-drawer/FileUploadDrawer'
import { ConflictModal } from '@/user_files/drive/components/ConflictModal'
import { ContextualActionBar, type ContextualAction } from '@/user_files/files/components/ContextualActionBar'
import { useDriveSelection } from '@/user_files/drive/hooks/useDriveSelection'
import { useStartChatWithSelectedFiles } from '@/user_files/files/hooks/useStartChatWithSelectedFiles'
import { useDeleteDialog } from '@/user_files/drive/hooks/useDeleteDialog'
import { useMoveDialog } from '@/user_files/drive/hooks/useMoveDialog'
import { useMoveConflict } from '@/user_files/drive/hooks/useMoveConflict'
import { useRenameDialog } from '@/user_files/drive/hooks/useRenameDialog'
import { useDriveUpload } from '@/user_files/drive/hooks/useDriveUpload'
import { useUploadDrawer } from '@/user_files/drive/hooks/useUploadDrawer'
import { useProcessingSummary } from '@/uppy-file-upload/hooks/useProcessingSummary'
import type { MatterFilesSearch } from '@/user_files/search/hooks/useMatterFilesSearch'
import { SearchResultsHeader } from '@/user_files/search/components/SearchResultsHeader'
import type { DriveListSorting } from '@/user_files/drive/hooks/useDriveListSort'
import { filterVisibleGroupedFiles, flatSearchGroupedFiles, groupExtractedFiles } from '@/user_files/utils/group-extracted-files'
import { countExtractedAttachmentsForSelection } from '@/user_files/utils/email-attachment-scope-copy'
import { useEmailAttachmentExpansion } from '@/user_files/hooks/useEmailAttachmentExpansion'
import { DriveSourceHeader, type DriveSourceHeaderProps } from './DriveSourceHeader'
import { MatterOcrQuotaStatus } from './MatterOcrQuotaStatus'

type MatterFilesPanelProps = {
  activeMatterId: string
  headerProps: DriveSourceHeaderProps
  /** Setup prompt rendered directly below the header row; the drive owns what it says. */
  banner?: ReactNode
  /** Connection health beside the ordinary header actions. */
  headerTrailing?: ReactNode
  /** Opens a synced folder in the DMS source view, where its contents are actually listed. */
  onOpenSyncedFolder?: (folderId: string) => void
  /** Owned by the drive, since the search field spans both source views. */
  search: MatterFilesSearch
  /** Owned by the drive, since the sort is what its list fetches are keyed on. */
  sorting: DriveListSorting
}

export function MatterFilesPanel({ activeMatterId, headerProps, banner, headerTrailing, onOpenSyncedFolder, search, sorting }: MatterFilesPanelProps) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const currentFolderId = useAppSelector(selectCurrentFolderId)
  const folderDialogType = useAppSelector(selectFolderDialogType)
  const browseFiles = useAppSelector(selectCurrentFolderDriveVisibleFiles)
  const browseFolders = useAppSelector(selectCurrentFolderDriveVisibleChildren)
  const filesById = useAppSelector(selectFilesById)
  const isUploading = useAppSelector(selectIsUploadingInActiveMatter)
  // Tombstoned (sync-disconnected) folders are navigable, but the backend
  // external-storage guard rejects uploads and folder creation inside them.
  const isCurrentFolderTombstoned = useAppSelector(selectIsCurrentFolderTombstoned)

  // Search results replace the folder listing, so everything downstream — selection, move, delete —
  // operates on the rows currently on screen rather than on the current folder's contents.
  const searchFiles = useMemo(() => search.rows.flatMap((row) => (row.kind === 'file' ? [row.data] : [])), [search.rows])
  const searchFolders = useMemo(() => search.rows.flatMap((row) => (row.kind === 'folder' ? [row.data] : [])), [search.rows])
  const files = search.isActive ? searchFiles : browseFiles
  const folders = search.isActive ? searchFolders : browseFolders

  // A reorder invalidates the selection the same way a folder change does: the rows on screen are
  // no longer the rows that were selected against.
  const selectionResetKey = search.isActive ? search.submittedQuery : `${currentFolderId}::${sorting.sort.key}::${sorting.sort.direction}`
  const { expandedIds, isExpanded, toggleExpanded } = useEmailAttachmentExpansion(selectionResetKey ?? '')
  const toggleAttachments = useCallback((fileId: string) => () => toggleExpanded(fileId), [toggleExpanded])

  // The one place browse rows are built, so the table renders exactly what selection is computed
  // over — both derive from the same two selectors. Folders precede files under every sort: they
  // come from separate endpoints, so the merged listing is a concatenation, not an interleaving.
  // Sorting reorders roots only; expanded children stay under their parent email.
  const browseRows = useMemo<DriveRow[]>(() => {
    const grouped = groupExtractedFiles(browseFiles)
    return [
      ...browseFolders.map((data) => ({ kind: 'folder' as const, data })),
      ...filterVisibleGroupedFiles(grouped, expandedIds).map(({ file, visualDepth, parentName, hasChildren, childCount }) => ({
        kind: 'file' as const,
        data: file,
        attachmentDepth: visualDepth,
        attachmentParentName: parentName,
        hasExtractedChildren: hasChildren,
        extractedAttachmentCount: childCount,
        attachmentsExpanded: hasChildren ? isExpanded(file.id) : undefined,
        onToggleAttachments: hasChildren ? toggleAttachments(file.id) : undefined,
      })),
    ]
  }, [browseFolders, browseFiles, expandedIds, isExpanded, toggleAttachments])

  // Search stays flat (no expand tree). Surface parent email context and attachment counts from the result set.
  const searchRows = useMemo<DriveRow[]>(() => {
    const metaById = new Map(flatSearchGroupedFiles(searchFiles).map((row) => [row.file.id, row]))
    return search.rows.map((row) => {
      if (row.kind !== 'file') return row
      const meta = metaById.get(row.data.id)
      const extractedAttachmentCount = meta?.childCount ?? 0
      const parentId = row.data.extracted_from_file_id
      // Prefer grouping metadata (same-folder + cycle-safe); fall back to the store when the parent is outside the hit set.
      const parentName = meta?.parentName ?? (parentId ? filesById[parentId]?.original_filename : undefined)
      return {
        ...row,
        extractedAttachmentCount,
        ...(parentName ? { attachmentParentName: parentName, showAttachmentParentLabel: true as const } : {}),
      }
    })
  }, [search.rows, searchFiles, filesById])
  const rows: DriveRow[] = search.isActive ? searchRows : browseRows
  const ancestorFolderIdsById = search.isActive ? search.ancestorFolderIdsById : undefined

  // Pass the full folder/search file list, not only expanded rows: select-all and Ask Assistant
  // intentionally include collapsed extracted attachments (see useDriveSelection).
  const {
    selectedSelectableFileIds,
    selectedMutableFileIds,
    selectedFolderIds,
    clearSelection,
    isFileSelected,
    toggleFileSelect,
    isFolderSelected,
    toggleFolderSelect,
    selectedCount,
    isAllSelected,
    selectAll,
  } = useDriveSelection(files, folders, selectionResetKey)

  const {
    deleteDialog,
    openDeleteForSelected,
    openDeleteForFile,
    openDeleteForFolder,
    closeDeleteDialog,
    confirmDelete,
    confirmDeleteNotInUse,
    isDeleting,
    deleteError,
    preview: deletePreview,
    previewLoading: deletePreviewLoading,
  } = useDeleteDialog({
    files,
    folders,
    currentFolderId,
    selectedSelectableFileIds: selectedMutableFileIds,
    selectedFolderIds,
    clearSelection,
    ancestorFolderIdsById,
  })

  const { moveDialog, openMoveForSelected, moveSelectedDisabled, openMoveItems, closeMoveDialog, isSubmitting } = useMoveDialog({
    files,
    folders,
    currentFolderId,
    selectedSelectableFileIds: selectedMutableFileIds,
    selectedFolderIds,
    ancestorFolderIdsById,
  })

  // Sends the selection to the Assistant. A selected folder contributes the files inside it, so the
  // chat can receive more files than the toolbar's item count names — the toolbar counts what was
  // ticked, every surface downstream counts what it acts on.
  const { isStarting: isStartingAssistantChat, startChat: startAssistantChat } = useStartChatWithSelectedFiles({
    matterId: activeMatterId,
    files,
    selectedFileIds: selectedSelectableFileIds,
    selectedFolderIds,
  })

  const { checkAndConfirmMove, moveConflictDialog } = useMoveConflict({
    onComplete: clearSelection,
  })

  const { renameDialog, openRenameForFile, openRenameForFolder, closeRenameDialog, confirmRename, isRenaming, renameError, occupiedNames } = useRenameDialog()

  const {
    openUploadPicker,
    openFolderPicker,
    disabled: dropzoneDisabled,
    fileInputRef,
    folderInputRef,
    addFiles,
    onFilesPicked,
    cancelUpload,
    removeFile,
    hasQueuedFiles,
    conflictDialog,
  } = useDriveUpload({ folderId: currentFolderId })

  const { totalCount: processingTotalCount } = useProcessingSummary()
  const hasActiveUploads = processingTotalCount > 0 || hasQueuedFiles || isUploading

  const { visible, expanded, offsetForSidebar, onExpandedChange, onClose } = useUploadDrawer({
    processingTotalCount,
    hasActiveUploads,
  })

  const handleNewFolder = () => {
    dispatch(UserFoldersActions.openDialog({ dialogType: 'create', folderId: null }))
  }

  // Names the sort on screen rather than letting the fetchers fall back to the cached one: after a
  // failed load the caches still describe the last sort that succeeded, so a retry without it would
  // reload the previous order under the new header.
  const handleRetryBrowse = () => {
    if (!currentFolderId) return
    dispatch(UserFoldersActions.fetchFolderChildren({ folderId: currentFolderId, sort: sorting.sort }))
    dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolderId, sort: sorting.sort }))
  }

  // Folder upload needs a directory picker the browser actually honours; where it doesn't, Upload
  // stays a plain file-picker button rather than a menu with one usable item.
  const showUploadMenu = isDirectoryPickerSupported()
  // `dropzoneDisabled` covers an upload already in flight. Without it the button looks enabled and
  // silently does nothing, which as a menu trigger would open a menu whose items both no-op.
  const uploadAction: ActionBarAction = showUploadMenu
    ? {
        icon: <Upload className="h-4 w-4" />,
        label: 'Upload',
        disabled: dropzoneDisabled,
        menu: (
          <MenuItemGroup>
            <MenuItem label="File upload" icon={FileUpload} onSelect={openUploadPicker} variant="neutral" />
            <MenuItem label="Folder upload" icon={FolderUpload} onSelect={openFolderPicker} variant="neutral" />
          </MenuItemGroup>
        ),
      }
    : {
        icon: <Upload className="h-4 w-4" />,
        label: 'Upload',
        onClick: openUploadPicker,
        disabled: dropzoneDisabled,
      }

  // The count is the rows the user ticked: a selected folder is one item rather than the files
  // inside it, and a row inside a selected folder is still one the user ticked. Same rule as the
  // delete confirmation's headline, so the toolbar and that dialog can never disagree on one
  // selection. Grouped attachments are selectable for Ask Assistant only — Move/Delete stay on
  // mutable roots and folders.
  const canMutateSelection = selectedMutableFileIds.length > 0 || selectedFolderIds.length > 0
  const contextualActions: ContextualAction[] = [
    { label: 'Ask Assistant', icon: AiChat, onClick: startAssistantChat, loading: isStartingAssistantChat },
    ...(canMutateSelection
      ? [
          { label: 'Move', icon: MoveToFolder, onClick: moveSelectedDisabled ? undefined : openMoveForSelected },
          { label: 'Delete', icon: TrashDelete, onClick: openDeleteForSelected, destructive: true },
        ]
      : []),
  ]
  const contextualActionBar = <ContextualActionBar selectedCount={selectedCount} actions={contextualActions} onClearSelection={clearSelection} />

  return (
    <>
      <MatterOcrQuotaStatus
        deferNotice={conflictDialog.open || moveConflictDialog.open || deleteDialog.open || moveDialog.open || renameDialog.open || folderDialogType !== null}
      />

      {/* The design gives the header one 32px slot that holds exactly one of three bars: the
          breadcrumb row, the search-results header, or — whenever something is selected — the
          contextual toolbar. */}
      {selectedCount > 0 ? (
        contextualActionBar
      ) : (
        /* Header with breadcrumbs + Upload/Folder actions */
        <ActionBar
          trailing={headerTrailing}
          content={
            search.isActive && search.submittedQuery ? (
              <SearchResultsHeader query={search.submittedQuery} resultCount={search.rows.length} totalCount={search.totalCount} truncated={search.truncated} />
            ) : (
              <DriveSourceHeader {...headerProps} />
            )
          }
          // Neither action has a folder to act on: search results span the matter, and a detached
          // folder rejects writes. The design drops both from the row in either case.
          actions={
            search.isActive || isCurrentFolderTombstoned
              ? []
              : [
                  uploadAction,
                  {
                    icon: <NewFolderPlus className="h-4 w-4" />,
                    label: 'Folder',
                    onClick: handleNewFolder,
                  },
                ]
          }
        />
      )}

      {!search.isActive && banner}

      {/* `isCurrentFolderTombstoned` also gates the folder input: a folder upload there would fire
          POST /folders per path level into a sync-disconnected folder the backend guard rejects. */}
      <DriveDropzone
        disabled={dropzoneDisabled || isCurrentFolderTombstoned}
        fileInputRef={fileInputRef}
        folderInputRef={folderInputRef}
        addFiles={addFiles}
        onFilesPicked={onFilesPicked}
      >
        {/* Folder and file list */}
        <FolderList
          rows={rows}
          onRetryBrowse={handleRetryBrowse}
          sorting={sorting.sorting}
          onSortingChange={sorting.onSortingChange}
          selection={{
            isFileSelected,
            toggleFileSelect,
            openRenameForFile: (fileId) => {
              const file = files.find((f) => f.id === fileId)
              if (file) openRenameForFile(file)
            },
            openDeleteForFile,
            openMoveItems,
            isFolderSelected,
            toggleFolderSelect,
            openRenameForFolder: (folderId) => {
              const folder = folders.find((f) => f.id === folderId)
              if (folder) openRenameForFolder(folder)
            },
            openDeleteForFolder,
            isAllSelected,
            selectAll,
          }}
          searchResults={
            search.isActive && search.submittedQuery
              ? {
                  query: search.submittedQuery,
                  status: search.status,
                  error: search.error,
                  onRetry: search.retry,
                  onClearSearch: search.clearSearch,
                  onOpenSyncedFolder,
                  onOpenMatterFolder: (folderId) => {
                    // Remember the destination before route resolution can unmount the drive.
                    headerProps.onChangeViewMode('matter')
                    search.clearSearch()
                    if (folderId !== currentFolderId) navigate(matterPaths.userFiles.folder(activeMatterId, folderId))
                  },
                }
              : undefined
          }
        />

        {/*
          Stays a React child of the dropzone (so drag events still bubble to it), but renders
          into document.body so it escapes the content viewer's `inert` subtree — on tablet/mobile
          the viewer is a Headless UI Dialog that inerts the whole app tree while open, which would
          otherwise make the fixed drawer non-interactive (can't expand/collapse or remove rows).
        */}
        {visible &&
          createPortal(
            <FileUploadDrawer
              expanded={expanded}
              onExpandedChange={onExpandedChange}
              onClose={onClose}
              onCancel={cancelUpload}
              onDismiss={removeFile}
              offsetForSidebar={offsetForSidebar}
            />,
            document.body
          )}
      </DriveDropzone>

      {/* Dialogs (conditionally rendered based on dialog state) */}
      <CreateFolderDialog />
      <RenameItemDialog
        open={renameDialog.open}
        item={renameDialog.item}
        isRenaming={isRenaming}
        error={renameError}
        occupiedNames={occupiedNames}
        onClose={closeRenameDialog}
        onConfirm={async (newName) => {
          await confirmRename(newName)
          // Rows come from the search response, not the store, so a renamed row would keep its old
          // name until the query is re-run. Rename is not a batched operation, so the batching
          // refresh does not cover it.
          if (search.isActive) search.retry()
        }}
      />
      <DeleteItemsDialog
        open={deleteDialog.open}
        fileCount={deleteDialog.fileCount}
        folderCount={deleteDialog.folderCount}
        extractedAttachmentCount={countExtractedAttachmentsForSelection(files, deleteDialog.fileIds)}
        preview={deletePreview}
        previewLoading={deletePreviewLoading}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        onConfirmNotInUse={confirmDeleteNotInUse}
        isDeleting={isDeleting}
        error={deleteError}
      />
      <ConflictModal {...conflictDialog} operation="upload" />
      {moveDialog.open && currentFolderId && (
        <MoveItemsDialog
          open
          initialMatterId={activeMatterId}
          initialFolderId={currentFolderId}
          sourceFolderId={moveDialog.sourceFolderId}
          fileIds={moveDialog.fileIds}
          folderIds={moveDialog.folderIds}
          selectedFileCount={moveDialog.selectedFileCount}
          selectedFolderCount={moveDialog.selectedFolderCount}
          extractedAttachmentCount={countExtractedAttachmentsForSelection(files, moveDialog.fileIds)}
          submitting={isSubmitting}
          error={null}
          onClose={closeMoveDialog}
          onConfirm={(destinationFolderId) => {
            checkAndConfirmMove({
              fileIds: moveDialog.fileIds,
              folderIds: moveDialog.folderIds,
              refreshFolderId: currentFolderId,
              destinationFolderId,
              filePreconditions: moveDialog.filePreconditions,
              folderPreconditions: moveDialog.folderPreconditions,
              selectedItems: moveDialog.selectedFileCount + moveDialog.selectedFolderCount,
            })
            closeMoveDialog()
          }}
        />
      )}
      <ConflictModal {...moveConflictDialog} operation="move" />
    </>
  )
}
